import { type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastContext, ToastEnum } from '@furality/furality-purpose';
import AuctionNotificationToastBody from '@/components/_auctions/AuctionNotificationToastBody.tsx';
import PartyInviteToastBody from '@/components/PartyInviteToastBody.tsx';
import useLanguage from '@/lib/hooks/useLanguage.ts';

const useToasts = () => {
  const { pushToast: origPushToast, ...context } = useContext(ToastContext);

  const { t } = useTranslation();
  const { language } = useLanguage();

  const pushToast = (type: ToastEnum, message: ReactNode, duration?: number) =>
    origPushToast(type, t(type === ToastEnum.Error ? 'toast.error' : 'toast.info'), message, {
      duration,
      distanceFormatLocale: language.dateFnsLocale,
    });

  const pushPartyToast = (requesterVrchatId: string, requesterDisplayName: string): string =>
    context.pushRawToast(PartyInviteToastBody, {
      requesterVrchatId,
      requesterDisplayName,
      timestamp: new Date().valueOf(),
    });

  const pushAuctionToast = ({
    auctionId,
    title,
    message,
    options,
  }: {
    auctionId?: string;
    title: ReactNode;
    message: ReactNode;
    options?: { linkToList?: boolean };
  }): string =>
    context.pushRawToast(AuctionNotificationToastBody, {
      auctionId,
      title,
      message,
      linkToList: options?.linkToList,
      timestamp: new Date().valueOf(),
    });

  return {
    ...context,
    pushToast,
    pushPartyToast,
    pushAuctionToast,
  };
};

export default useToasts;
